// Şoförler ve rehberler (C31–C37): Zod girdileri ve yanıt tipleri (API ve konsol aynı tipi kullanır).
// Belge kayıtlarının kendisi filo alanının belge uçlarındadır (`/v1/documents`); burada yalnız özet (doğrulama rozeti) var.
import { z } from 'zod';
import type { BoardingMethod, ComplaintCategory, ComplaintStatus, DocumentType, Direction, TripStatus } from '../enums';
import type { TripDisplay } from '../state';
import { IsoDate, Phone } from './common';

// ───────── durum ve sözlük ─────────

export const STAFF_STATUSES = ['ACTIVE', 'ON_LEAVE', 'INACTIVE'] as const;
export type StaffStatus = (typeof STAFF_STATUSES)[number];
export const STAFF_STATUS_LABELS: Record<StaffStatus, string> = {
  ACTIVE: 'Aktif',
  ON_LEAVE: 'İzinli',
  INACTIVE: 'Pasif',
};

/** Servis için geçerli ehliyet sınıfları (D1 minibüs, D otobüs). */
export const LICENSE_CLASSES = ['B', 'D1', 'D1E', 'D', 'DE'] as const;

/** Doğrulama rozetini belirleyen yasal belgeler. Fotoğraf profil öğesidir, rozeti düşürmez. */
export const DRIVER_REQUIRED_DOCUMENTS: DocumentType[] = [
  'LICENSE',
  'SRC',
  'PSYCHOTECHNIC',
  'ID_CARD',
  'CRIMINAL_RECORD',
];
export const GUIDE_REQUIRED_DOCUMENTS: DocumentType[] = ['ID_CARD', 'CRIMINAL_RECORD'];
/** Rehberden istenebilecek belgeler (şoför listesi `DRIVER_DOCUMENTS`). */
export const GUIDE_DOCUMENTS: DocumentType[] = ['ID_CARD', 'PHOTO', 'CRIMINAL_RECORD', 'PSYCHOTECHNIC'];

/** Belge doğrulama özeti: en kötü durum kazanır (dolmuş > eksik > yaklaşan > onay bekleyen > tamam). */
export const STAFF_DOCS_STATES = ['EXPIRED', 'MISSING', 'EXPIRING', 'PENDING', 'OK'] as const;
export type StaffDocsState = (typeof STAFF_DOCS_STATES)[number];
export const STAFF_DOCS_LABELS: Record<StaffDocsState, string> = {
  OK: 'Belgeler tamam',
  PENDING: 'Onay bekliyor',
  EXPIRING: 'Süresi yaklaşıyor',
  MISSING: 'Belge eksik',
  EXPIRED: 'Süresi dolmuş',
};

export interface StaffDocs {
  state: StaffDocsState;
  missing: DocumentType[];
  expired: DocumentType[];
  expiring: { type: DocumentType; daysLeft: number }[];
  /** incelemeyi bekleyen yüklemeler */
  pending: number;
  /** zorunlu belgelerden geçerli olanlar */
  valid: number;
  required: number;
}

// ───────── ortak parçalar ─────────

export interface StaffTrip {
  id: string;
  date: string;
  direction: Direction;
  status: TripStatus;
  display: TripDisplay;
  plannedStartAt: string;
  plannedEndAt: string;
  actualStartAt: string | null;
  delayMin: number;
  routeId: string;
  routeCode: string;
  routeName: string;
  institution: string;
  vehicleId: string;
  plate: string;
  /** rehber ekranında şoför, şoför ekranında rehber */
  partner: string | null;
}

/** Takvimdeki düzenli görev (rota × yön × araç). */
export interface StaffAssignment {
  scheduleId: string;
  routeId: string;
  routeCode: string;
  routeName: string;
  direction: Direction;
  departureTime: string;
  daysOfWeek: number[];
  institution: string;
  vehicleId: string;
  plate: string;
  /** rehber ekranında şoför, şoför ekranında rehber */
  partner: string | null;
}

export interface StaffApp {
  /** uygulama hesabı (telefonla) bağlı */
  linked: boolean;
  lastLoginAt: string | null;
  lastInviteAt: string | null;
}

export interface StaffToday {
  trips: number;
  done: number;
  routeCodes: string[];
  plate: string | null;
  /** şu an yolda / sıradaki sefer */
  current: { tripId: string; routeCode: string; plannedStartAt: string; display: TripDisplay; delayMin: number } | null;
}

// ───────── şoför listesi (C31) ─────────

export const DriverListQuery = z.object({
  q: z.string().trim().max(80).optional(),
  status: z.enum(STAFF_STATUSES).optional(),
  docs: z.enum(['ok', 'issue']).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type DriverListQuery = z.infer<typeof DriverListQuery>;

export interface DriverRow {
  id: string;
  firstName: string;
  lastName: string;
  employeeNo: string | null;
  phoneMasked: string;
  licenseClass: string | null;
  status: StaffStatus;
  photoUrl: string | null;
  docs: StaffDocs;
  today: StaffToday;
  /** son 30 gün, tamamlanan seferlerde gecikme eşiği altı */
  onTimePct: number | null;
  completed30: number;
  rating: number | null;
  ratingCount: number;
  complaints30: number;
  appLinked: boolean;
}

export interface DriverListCounts {
  all: number;
  active: number;
  onLeave: number;
  inactive: number;
  docIssues: number;
  onDutyToday: number;
}

export type DriverList = {
  items: DriverRow[];
  total: number;
  page: number;
  pageSize: number;
  counts: DriverListCounts;
};

// ───────── şoför detayı (C32) ─────────

export interface StaffComplaint {
  id: string;
  number: string;
  category: ComplaintCategory;
  status: ComplaintStatus;
  createdAt: string;
  tripId: string | null;
}

export interface StaffIncident {
  id: string;
  type: string;
  severity: string;
  status: string;
  note: string | null;
  createdAt: string;
  tripId: string;
  routeCode: string;
}

export interface DriverDetail extends DriverRow {
  /** tam numara yalnız düzenleme yetkisinde (form için); ekranda maskeli gösterilir */
  phone: string | null;
  hiredAt: string | null;
  createdAt: string;
  app: StaffApp;
  vehicle: { id: string; plate: string; model: string } | null;
  assignments: StaffAssignment[];
  todayTrips: StaffTrip[];
  recentTrips: StaffTrip[];
  /** önümüzdeki 7 günde planlı sefer (durum değişikliği uyarısı) */
  upcoming: number;
  inProgress: boolean;
  stats30: {
    trips: number;
    cancelled: number;
    avgDelayMin: number | null;
    km: number;
    incidents: number;
    openComplaints: number;
  };
  complaints: StaffComplaint[];
  incidents: StaffIncident[];
}

// ───────── şoför performansı (C35) ─────────

export const PERFORMANCE_PERIODS = [7, 30, 90] as const;
export const PerformanceQuery = z.object({
  days: z.coerce
    .number()
    .int()
    .refine((d) => (PERFORMANCE_PERIODS as readonly number[]).includes(d), 'Dönem 7, 30 veya 90 gün olmalı')
    .default(30),
});

export interface DriverPerformance {
  driverId: string;
  name: string;
  days: number;
  from: string;
  to: string;
  delayWarnMin: number;
  totals: {
    trips: number;
    completed: number;
    cancelled: number;
    onTimePct: number | null;
    avgDelayMin: number | null;
    km: number;
    boardings: number;
    /** QR / PIN / NFC / teslim kodu ile doğrulanan binişlerin payı */
    verifiedPct: number | null;
    rating: number | null;
    ratingCount: number;
    complaints: number;
    incidents: number;
  };
  /** önceki eşit dönem (karşılaştırma oku) */
  previous: { onTimePct: number | null; avgDelayMin: number | null; rating: number | null };
  /** firmadaki tüm şoförler (aynı dönem) */
  company: { onTimePct: number | null; rating: number | null; avgDelayMin: number | null };
  /** gün (7/30) veya hafta (90) kovaları */
  series: {
    label: string;
    from: string;
    trips: number;
    onTime: number;
    late: number;
    veryLate: number;
    onTimePct: number | null;
    avgDelayMin: number | null;
  }[];
  delayBuckets: { key: 'ON_TIME' | 'LATE' | 'VERY_LATE'; label: string; trips: number }[];
  byRoute: { routeId: string; routeCode: string; routeName: string; trips: number; onTimePct: number | null }[];
  ratingDist: { stars: number; count: number }[];
  /** 4–5 yıldızda övülen, 1–3 yıldızda sorun bildirilen etiketler */
  ratingTags: { tag: string; praised: number; criticized: number }[];
  comments: { stars: number; comment: string; createdAt: string; routeCode: string | null }[];
  complaintsByCategory: { category: ComplaintCategory; count: number }[];
  complaints: StaffComplaint[];
  incidents: StaffIncident[];
  boardingMethods: { method: BoardingMethod; count: number }[];
}

// ───────── rehberler (C36/C37) ─────────

export const GuideListQuery = z.object({
  q: z.string().trim().max(80).optional(),
  status: z.enum(STAFF_STATUSES).optional(),
  docs: z.enum(['ok', 'issue']).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type GuideListQuery = z.infer<typeof GuideListQuery>;

export interface GuideRow {
  id: string;
  firstName: string;
  lastName: string;
  phoneMasked: string;
  status: StaffStatus;
  photoUrl: string | null;
  docs: StaffDocs;
  today: StaffToday;
  routeCodes: string[];
  /** düzenli görev yaptığı hatlardaki öğrenci sayısı */
  students: number;
  appLinked: boolean;
}

export type GuideList = {
  items: GuideRow[];
  total: number;
  page: number;
  pageSize: number;
  counts: { all: number; active: number; onLeave: number; inactive: number; docIssues: number; onDutyToday: number };
};

export interface GuideDetail extends GuideRow {
  phone: string | null;
  createdAt: string;
  app: StaffApp;
  assignments: StaffAssignment[];
  todayTrips: StaffTrip[];
  recentTrips: StaffTrip[];
  upcoming: number;
  inProgress: boolean;
  stats30: {
    trips: number;
    /** rehber onayıyla kaydedilen biniş/iniş/teslim olayları */
    confirmations: number;
    deliveries: number;
    incidents: number;
  };
  incidents: StaffIncident[];
}

// ───────── girdiler (C33 ve rehber formu) ─────────

const Name = (what: string) =>
  z.string().trim().min(2, `${what} en az 2 harf olmalı`).max(60, `${what} en fazla 60 harf olabilir`);

export const DriverInput = z.object({
  firstName: Name('Ad'),
  lastName: Name('Soyad'),
  phone: Phone,
  employeeNo: z.string().trim().max(20, 'Sicil no en fazla 20 karakter olabilir').optional().nullable(),
  licenseClass: z.enum(LICENSE_CLASSES).optional().nullable(),
  hiredAt: IsoDate.optional().nullable(),
  /** oluştururken uygulama daveti SMS'i */
  sendInvite: z.boolean().default(true),
});
export type DriverInput = z.input<typeof DriverInput>;

export const DriverUpdateInput = DriverInput.omit({ sendInvite: true }).partial();
export type DriverUpdateInput = z.input<typeof DriverUpdateInput>;

export const GuideInput = z.object({
  firstName: Name('Ad'),
  lastName: Name('Soyad'),
  phone: Phone,
  sendInvite: z.boolean().default(true),
});
export type GuideInput = z.input<typeof GuideInput>;

export const GuideUpdateInput = GuideInput.omit({ sendInvite: true }).partial();
export type GuideUpdateInput = z.input<typeof GuideUpdateInput>;

export const StaffStatusInput = z.object({
  status: z.enum(STAFF_STATUSES),
  note: z.string().trim().max(200).optional(),
});
export type StaffStatusInput = z.infer<typeof StaffStatusInput>;

export interface StaffSaveResult {
  id: string;
  appLinked: boolean;
  invited: boolean;
  /** uygulama hesabı bağlanamadıysa nedeni (ör. numara başka firmada şoför) */
  notice: string | null;
}

export interface StaffInviteResult {
  sentTo: string;
  at: string;
}
